/**
 * HeroSection — top-of-page hero with animated orbs, headline, CTAs,
 * avatar stack with writer count, and a floating preview card.
 *
 * The mouse-parallax effect is wired up via useHeroParallax on the outer ref.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { landingTokens as T } from '@codex/theme';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import BookmarkIcon from '@mui/icons-material/Bookmark';

import {
  BtnPrimary,
  BtnGhost,
  Hero,
  HeroBg,
  HeroOrb,
  HeroGrain,
  HeroInner,
  HeroEyebrow,
  EyebrowDot,
  HeroHeadline,
  HeadlineShimmer,
  HeroSub,
  HeroActions,
  AvatarStack,
  AvatarBubble,
  AvatarCount,
  HeroVisual,
  HeroCard,
  HeroCardHeader,
  CardDots,
  CardTitle,
  HeroCardContent,
  CardMeta,
  CardAvatar,
  CardAuthorName,
  CardDate,
  CardBadge,
  CardLines,
  CardLine,
  CardStats,
  FloatEarnings,
  FloatSubs,
  FloatLabel,
  FloatValue,
  FloatTrend,
} from '../../common/LandingPage.styled';
import { useHeroParallax } from '../../common/useHeroParallax';
import { VellumProIconFlat } from '../../../../components/brand';
import type { RoundedStat } from '../../common/stats';

const AVATAR_INITIALS = ['ER', 'SA', 'MK', 'JL', 'TW'];
const CARD_LINE_WIDTHS = [92, 85, 97, 78, 91, 55];

export interface HeroSectionProps {
  /** Rounded writer count — used in the avatar-stack copy. */
  writers: RoundedStat;
}

export function HeroSection({ writers }: HeroSectionProps) {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);
  useHeroParallax(heroRef);

  return (
    <Hero ref={heroRef}>
      <HeroBg>
        <HeroOrb variant={1} />
        <HeroOrb variant={2} />
        <HeroOrb variant={3} />
        <HeroGrain />
      </HeroBg>

      <HeroInner>
        <HeroEyebrow>
          <EyebrowDot />
          {t('landing.hero.eyebrow')}
        </HeroEyebrow>
        <HeroHeadline>
          {t('landing.hero.headline1')}
          <br />
          <HeadlineShimmer>{t('landing.hero.headline2')}</HeadlineShimmer>
        </HeroHeadline>
        <HeroSub>{t('landing.hero.sub')}</HeroSub>
        <HeroActions>
          <BtnPrimary to="/register">
            <strong>{t('landing.hero.cta')}</strong>
          </BtnPrimary>
          <BtnGhost to="#features">
            {t('landing.hero.secondaryCta')} <ArrowDownwardIcon />
          </BtnGhost>
        </HeroActions>
        <AvatarStack>
          {AVATAR_INITIALS.map((init, i) => (
            <AvatarBubble key={init} index={i}>
              {init}
            </AvatarBubble>
          ))}
          <AvatarCount>
            {t('landing.hero.writersCount', {
              count: writers.value.toLocaleString(),
            })}
          </AvatarCount>
        </AvatarStack>
      </HeroInner>

      <HeroVisual>
        <HeroCard>
          <HeroCardHeader>
            <CardDots>
              <span />
              <span />
              <span />
            </CardDots>
            <CardTitle>{t('landing.heroCard.title')}</CardTitle>
          </HeroCardHeader>
          <HeroCardContent>
            <CardMeta>
              <CardAvatar>EP</CardAvatar>
              <div>
                <CardAuthorName>{t('landing.heroCard.author')}</CardAuthorName>
                <CardDate>{t('landing.heroCard.meta')}</CardDate>
              </div>
              <CardBadge>{t('landing.heroCard.badge')}</CardBadge>
            </CardMeta>
            <CardLines>
              {CARD_LINE_WIDTHS.map((w, i) => (
                <CardLine key={i} width={`${w}%`} />
              ))}
            </CardLines>
            <CardStats>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FavoriteIcon /> 2.4k
              </Typography>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BookmarkIcon /> 891
              </Typography>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CommentIcon /> 143
              </Typography>
            </CardStats>
          </HeroCardContent>
        </HeroCard>
        <FloatEarnings>
          <FloatLabel>{t('landing.heroCard.floatLabel')}</FloatLabel>
          <FloatValue>{t('landing.heroCard.floatValue')}</FloatValue>
          <FloatTrend>
            <ArrowUpwardIcon /> {t('landing.heroCard.floatTrend')}
          </FloatTrend>
        </FloatEarnings>
        <FloatSubs>
          <VellumProIconFlat size={18} color="#BAA87B" />
          <span style={{ fontSize: 13, color: T.text, whiteSpace: 'nowrap' }}>
            {t('landing.heroCard.newSubscriber')}
          </span>
        </FloatSubs>
      </HeroVisual>
    </Hero>
  );
}

export default HeroSection;
